import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MetaIntegration } from '@feathr/blackbox';
import { ConfirmModal, ContextMenu, toast } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import { errorMessage, getIconForAction, logError, useToggle } from '@feathr/hooks';

import { useMetaOAuth } from '../../PreConnection/MetaConnectButton/MetaConnectButton.hooks';

interface IMetaSettingsButtonProps {
  integration: MetaIntegration;
  onDisconnect: () => Promise<void>;
}

function MetaSettingsButton({
  integration,
  onDisconnect,
}: Readonly<IMetaSettingsButtonProps>): JSX.Element {
  const { t } = useTranslation();
  const [showModal, toggleModal] = useToggle(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { initiateOAuth, isLoading } = useMetaOAuth();
  const user = useUser();

  async function handleSync(): Promise<void> {
    setIsSyncing(true);
    try {
      await integration.syncPages();
    } catch (error) {
      const errorMsg = errorMessage(error, t);
      const isNoConnectionsError = errorMsg.includes('No active Meta connections');

      if (isNoConnectionsError) {
        toast(
          t(
            'Cannot sync: no active Meta connections found. Try reconnecting your Meta integration.',
          ),
          { type: 'error' },
        );
        return;
      }

      logError({
        message: 'Meta integration page sync failed',
        level: 'error',
        extra: {
          error: errorMsg,
          integrationId: integration?.id,
        },
      });
      toast(
        t('Something went wrong while syncing your Meta pages: {{error}}', {
          error: errorMsg,
        }),
        { type: 'error' },
      );
      return;
    } finally {
      setIsSyncing(false);
    }

    toast(t('Meta page sync queued. New pages may take a few minutes to appear.'), {
      type: 'success',
    });
  }

  async function handleDisconnect(): Promise<void> {
    try {
      await integration.disconnect();
    } catch (error) {
      const errorMsg = errorMessage(error, t);
      logError({
        message: 'Meta integration disconnect failed',
        level: 'error',
        extra: {
          error: errorMsg,
          integrationId: integration?.id,
        },
      });
      toast(
        t('Something went wrong while trying to disconnect your Meta integration: {{error}}', {
          error: errorMsg,
        }),
        { type: 'error' },
      );
      toggleModal();
      return;
    }

    toast(t('Your Meta integration has been successfully disconnected'), { type: 'success' });
    toggleModal();
    await onDisconnect();
  }

  return (
    <>
      <ContextMenu
        buttonText={t('Settings')}
        buttonType={'secondary'}
        disabled={isLoading}
        name={'meta-settings'}
        suffix={getIconForAction('settings')}
      >
        <ContextMenu.Item onClick={initiateOAuth} prefix={getIconForAction('configure')}>
          {t('Reconfigure')}
        </ContextMenu.Item>
        {user.isSudoer && (
          <ContextMenu.Item
            disabled={isSyncing}
            onClick={handleSync}
            prefix={getIconForAction('recalculate')}
          >
            {t('Synchronize')}
          </ContextMenu.Item>
        )}
        <ContextMenu.Divider />
        <ContextMenu.Item
          onClick={toggleModal}
          prefix={getIconForAction('disconnect')}
          theme={'danger'}
        >
          {t('Disconnect')}
        </ContextMenu.Item>
      </ContextMenu>
      <ConfirmModal
        alertTitle={t('You will lose the ability to publish Meta campaigns.')}
        alertType={'danger'}
        confirmButtonText={t('Disconnect')}
        confirmButtonType={'danger'}
        onClose={toggleModal}
        onConfirm={handleDisconnect}
        opened={showModal}
        t={t}
        title={t('Disconnect Meta integration?')}
      />
    </>
  );
}

export default MetaSettingsButton;
